import axios from 'axios';
import env from 'react-dotenv';
import datas from '../Assets/data.json';
import { Crypter } from './Security/Crypter';
import { clearSessionStorage } from './storage';

/**
 * Gestion de session back office (contrat Auth v2) :
 *  - login  : Result[0][0] = utilisateur { token, TokenExpireLe, RefreshToken, RefreshExpireLe }
 *  - refresh: POST /api/Auth/Refresh  { CodeU, RefreshToken } — prolonge la session
 *  - logout : POST /api/Auth/Logout   { CodeU, RefreshToken } — révoque la session
 */

const SESSION_KEY = 'eb-session';

/** Minutes restantes avant expiration d'un jeton (TokenExpireLe | ExpiredDate). */
export function tokenExpiresInMinutes(u: any): number {
    const raw = u?.TokenExpireLe || u?.ExpiredDate;
    const t = raw ? new Date(raw).getTime() : NaN;
    if (isNaN(t)) return 60;
    return Math.max(1, Math.round((t - Date.now()) / 60000));
}

/** Persiste le couple refresh + expirations (chiffré, clé non préservée → effacée au logout). */
export function saveSessionTokens(u: any) {
    if (!u?.RefreshToken) return;
    const payload = {
        CodeU: u.CodeU,
        RefreshToken: u.RefreshToken,
        RefreshExpireLe: u.RefreshExpireLe,
        TokenExpireLe: u.TokenExpireLe || u.ExpiredDate,
    };
    localStorage.setItem(SESSION_KEY, Crypter.encrypt(JSON.stringify(payload)));
}

export function getSessionTokens(): { CodeU?: string; RefreshToken?: string; RefreshExpireLe?: string; TokenExpireLe?: string } | null {
    try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return null;
        return JSON.parse(Crypter.decrypt(raw));
    } catch { return null; }
}

/** POST chiffré générique → Value déchiffré. */
function postAuth(url: string, payload: any, authHeader?: string): Promise<any> {
    return axios.request({
        method: 'post',
        maxBodyLength: Infinity,
        url: env.API_BASE_URL + url,
        headers: {
            'Content-Type': 'application/json',
            ...(authHeader ? { 'Authorization': authHeader } : {}),
        },
        data: Crypter.encrypt(JSON.stringify(payload)),
    }).then((r: any) => JSON.parse(Crypter.decrypt(r.data)).Value);
}

/** POST /api/Auth/Refresh — prolonge la session (nouveau jeton + nouveau refresh). */
export function refreshSession(authHeader?: string): Promise<any> {
    const s = getSessionTokens();
    if (!s?.RefreshToken) return Promise.resolve(null);
    return postAuth(datas.API_LINKs.Auth.Refresh, {
        CodeU: s.CodeU,
        RefreshToken: s.RefreshToken,
    }, authHeader);
}

/** POST /api/Auth/Logout — révoque la session côté serveur (best effort). */
function revokeSession(authHeader?: string) {
    const s = getSessionTokens();
    if (!s?.RefreshToken) return;
    postAuth(datas.API_LINKs.Auth.Logout, {
        CodeU: s.CodeU,
        RefreshToken: s.RefreshToken,
    }, authHeader).catch(() => { /* révocation best effort — le nettoyage local prime */ });
}

/** Déconnexion complète : révocation serveur + purge locale + signOut react-auth-kit. */
export function performLogout(signOut: () => boolean | void, authHeader?: string) {
    revokeSession(authHeader);
    clearSessionStorage();
    signOut();
}
